import React, { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { Save, Globe, Link2, Settings2, Plus, Trash2, Mail, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const CMSTab = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/cms/settings`, { headers, withCredentials: true });
      setSettings(res.data);
    } catch { toast.error('Failed to load CMS settings'); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/cms/settings`, settings, { headers, withCredentials: true });
      setSettings(res.data);
      toast.success('Settings saved successfully');
    } catch { toast.error('Failed to save settings'); }
    setSaving(false);
  };

  const updateField = (field, value) => {
    setSettings(s => ({ ...s, [field]: value }));
  };

  const updateStat = (idx, field, value) => {
    setSettings(s => {
      const stats = [...(s.stats || [])];
      stats[idx] = { ...stats[idx], [field]: value };
      return { ...s, stats };
    });
  };

  const updateListItem = (field, idx, value) => {
    setSettings(s => {
      const list = [...(s[field] || [])];
      list[idx] = value;
      return { ...s, [field]: list };
    });
  };

  const addListItem = (field) => {
    setSettings(s => ({ ...s, [field]: [...(s[field] || []), ''] }));
  };

  const removeListItem = (field, idx) => {
    setSettings(s => ({ ...s, [field]: (s[field] || []).filter((_, i) => i !== idx) }));
  };

  const updateIntegration = (integration, field, value) => {
    setSettings(s => ({
      ...s,
      integrations: {
        ...s.integrations,
        [integration]: { ...(s.integrations?.[integration] || {}), [field]: value },
      },
    }));
  };

  const updatePricing = (field, value) => {
    setSettings(s => ({
      ...s,
      pricing: { ...(s.pricing || {}), [field]: value },
    }));
  };

  const updatePricingAmount = (field, value) => {
    const num = parseInt(String(value).replace(/[^0-9]/g, ''), 10) || 0;
    updatePricing(field, num * 100);
  };

  if (loading || !settings) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="cms-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Website CMS</h2>
          <p className="text-sm text-slate-500 mt-1">Manage landing page content, integrations, and settings</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="cms-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      <Tabs value={activeSection} onValueChange={setActiveSection}>
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="hero" data-testid="cms-hero-tab"><Globe className="w-3 h-3 mr-1.5" />Hero</TabsTrigger>
          <TabsTrigger value="why" data-testid="cms-why-tab">Why EdAssist</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="cms-pricing-tab">Pricing</TabsTrigger>
          <TabsTrigger value="cta" data-testid="cms-cta-tab">CTA & Footer</TabsTrigger>
          <TabsTrigger value="links" data-testid="cms-links-tab"><Link2 className="w-3 h-3 mr-1.5" />Links</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="cms-integrations-tab"><Settings2 className="w-3 h-3 mr-1.5" />Integrations</TabsTrigger>
        </TabsList>

        <TabsContent value="hero" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Hero Section</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Badge Text</Label><Input value={settings.hero_badge || ''} onChange={(e) => updateField('hero_badge', e.target.value)} className="mt-1" data-testid="cms-hero-badge" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Title</Label><Input value={settings.hero_title || ''} onChange={(e) => updateField('hero_title', e.target.value)} className="mt-1" data-testid="cms-hero-title" /></div>
                <div><Label>Highlight Word</Label><Input value={settings.hero_highlight || ''} onChange={(e) => updateField('hero_highlight', e.target.value)} className="mt-1" data-testid="cms-hero-highlight" /></div>
              </div>
              <div><Label>Description</Label><Textarea value={settings.hero_description || ''} onChange={(e) => updateField('hero_description', e.target.value)} rows={3} className="mt-1" data-testid="cms-hero-desc" /></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Statistics</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {(settings.stats || []).map((stat, idx) => (
                <div key={idx} className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Value</Label><Input value={stat.value} onChange={(e) => updateStat(idx, 'value', e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs">Label</Label><Input value={stat.label} onChange={(e) => updateStat(idx, 'label', e.target.value)} className="mt-1" /></div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="why" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Why Choose EdAssist Section</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Section Title</Label><Input value={settings.why_edassist_title || ''} onChange={(e) => updateField('why_edassist_title', e.target.value)} className="mt-1" /></div>
              <div><Label>Section Subtitle</Label><Textarea value={settings.why_edassist_subtitle || ''} onChange={(e) => updateField('why_edassist_subtitle', e.target.value)} rows={2} className="mt-1" /></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">For Job Seekers Features</CardTitle>
                <Button variant="outline" size="sm" onClick={() => addListItem('job_seeker_features')}><Plus className="w-3 h-3 mr-1" />Add</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {(settings.job_seeker_features || []).map((f, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input value={f} onChange={(e) => updateListItem('job_seeker_features', idx, e.target.value)} />
                  <Button variant="ghost" size="sm" onClick={() => removeListItem('job_seeker_features', idx)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">For Employers Features</CardTitle>
                <Button variant="outline" size="sm" onClick={() => addListItem('employer_features')}><Plus className="w-3 h-3 mr-1" />Add</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {(settings.employer_features || []).map((f, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input value={f} onChange={(e) => updateListItem('employer_features', idx, e.target.value)} />
                  <Button variant="ghost" size="sm" onClick={() => removeListItem('employer_features', idx)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pricing" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Pricing Page Header</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Headline</Label><Input value={settings.pricing?.headline || ''} onChange={(e) => updatePricing('headline', e.target.value)} className="mt-1" data-testid="cms-pricing-headline" /></div>
              <div><Label>Sub-headline</Label><Input value={settings.pricing?.subheadline || ''} onChange={(e) => updatePricing('subheadline', e.target.value)} className="mt-1" /></div>
              <div><Label>Description</Label><Input value={settings.pricing?.description || ''} onChange={(e) => updatePricing('description', e.target.value)} className="mt-1" /></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Plan Names</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-4 gap-3">
                <div><Label className="text-xs">Free Plan</Label><Input value={settings.pricing?.free_name || 'Free'} onChange={(e) => updatePricing('free_name', e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Pack Plan</Label><Input value={settings.pricing?.pack_name || 'Pack of Listings'} onChange={(e) => updatePricing('pack_name', e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Monthly Plan</Label><Input value={settings.pricing?.monthly_name || '1 Month Plan'} onChange={(e) => updatePricing('monthly_name', e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Yearly Plan</Label><Input value={settings.pricing?.yearly_name || '1 Year Plan'} onChange={(e) => updatePricing('yearly_name', e.target.value)} className="mt-1" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Pricing Amounts (in ₹)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs font-medium text-slate-700">Pack of Listings — Price per Listing</Label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-slate-500">₹</span>
                  <Input type="number" value={Math.round((settings.pricing?.pack_price_per_listing || 299900) / 100)} onChange={(e) => updatePricingAmount('pack_price_per_listing', e.target.value)} className="max-w-32" data-testid="cms-pack-price" />
                  <span className="text-xs text-slate-400">per listing</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-slate-700">1 Month Plan — Price</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-slate-500">₹</span>
                    <Input type="number" value={Math.round((settings.pricing?.monthly_amount || 1799900) / 100)} onChange={(e) => updatePricingAmount('monthly_amount', e.target.value)} className="max-w-32" data-testid="cms-monthly-price" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-700">1 Month — Original Price (for strikethrough)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-slate-500">₹</span>
                    <Input type="number" value={Math.round((settings.pricing?.monthly_original || 2000000) / 100)} onChange={(e) => updatePricingAmount('monthly_original', e.target.value)} className="max-w-32" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-slate-700">1 Year Plan — Price</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-slate-500">₹</span>
                    <Input type="number" value={Math.round((settings.pricing?.yearly_amount || 17999900) / 100)} onChange={(e) => updatePricingAmount('yearly_amount', e.target.value)} className="max-w-32" data-testid="cms-yearly-price" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-700">1 Year — Original Price (for strikethrough)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-slate-500">₹</span>
                    <Input type="number" value={Math.round((settings.pricing?.yearly_original || 24000000) / 100)} onChange={(e) => updatePricingAmount('yearly_original', e.target.value)} className="max-w-32" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cta" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Call-to-Action Section</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>CTA Title</Label><Input value={settings.cta_title || ''} onChange={(e) => updateField('cta_title', e.target.value)} className="mt-1" /></div>
              <div><Label>CTA Subtitle</Label><Input value={settings.cta_subtitle || ''} onChange={(e) => updateField('cta_subtitle', e.target.value)} className="mt-1" /></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Footer</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Footer Tagline</Label><Input value={settings.footer_tagline || ''} onChange={(e) => updateField('footer_tagline', e.target.value)} className="mt-1" /></div>
              <div><Label>Copyright Text</Label><Input value={settings.copyright_text || ''} onChange={(e) => updateField('copyright_text', e.target.value)} className="mt-1" /></div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="links" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Upskilling / Training</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Upskilling Page Title</Label>
                <Input value={settings.upskilling_title || ''} onChange={(e) => updateField('upskilling_title', e.target.value)} placeholder="Upskilling Courses from Wadhwani Foundation" className="mt-1" data-testid="cms-upskilling-title" />
              </div>
              <div>
                <Label>Upskilling Page Description</Label>
                <Textarea value={settings.upskilling_description || ''} onChange={(e) => updateField('upskilling_description', e.target.value)} placeholder="Access free upskilling courses..." rows={2} className="mt-1" data-testid="cms-upskilling-desc" />
              </div>
              <div>
                <Label>Upskilling Course Link (External URL)</Label>
                <Input value={settings.upskilling_link || ''} onChange={(e) => updateField('upskilling_link', e.target.value)} placeholder="https://www.wadhwanifoundation.org/programs" className="mt-1" data-testid="cms-upskilling-link" />
                <p className="text-xs text-slate-400 mt-1">This link is used on the Training page as the "Explore Courses" button destination.</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Social Media</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>LinkedIn URL</Label>
                <Input value={settings.social_linkedin || ''} onChange={(e) => updateField('social_linkedin', e.target.value)} placeholder="https://www.linkedin.com/company/edassist" className="mt-1" data-testid="cms-social-linkedin" />
              </div>
              <div>
                <Label>Instagram URL</Label>
                <Input value={settings.social_instagram || ''} onChange={(e) => updateField('social_instagram', e.target.value)} placeholder="https://www.instagram.com/edassist" className="mt-1" data-testid="cms-social-instagram" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Affiliate & External Links</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Zety Affiliate Link</Label>
                <Input value={settings.zety_affiliate_link || ''} onChange={(e) => updateField('zety_affiliate_link', e.target.value)} placeholder="https://zety.com/resume-builder?ref=edassist" className="mt-1" data-testid="cms-zety-link" />
                <p className="text-xs text-slate-400 mt-1">This link appears on the Resume Builder page. Update with your Zety affiliate/partner link.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="integrations" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-600" />
                <div>
                  <CardTitle className="text-base">ZeptoMail (Email Notifications)</CardTitle>
                  <p className="text-xs text-slate-500 mt-0.5">Transactional emails for invites, alerts, and notifications</p>
                </div>
                <Badge className={settings.integrations?.zeptomail?.enabled ? 'bg-green-100 text-green-700 ml-auto' : 'bg-slate-100 text-slate-500 ml-auto'}>
                  {settings.integrations?.zeptomail?.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div><Label>API Key / Send Mail Token</Label><Input type="password" value={settings.integrations?.zeptomail?.api_key || ''} onChange={(e) => updateIntegration('zeptomail', 'api_key', e.target.value)} placeholder="Enter ZeptoMail API key" className="mt-1" data-testid="cms-zeptomail-key" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>From Email</Label><Input value={settings.integrations?.zeptomail?.from_email || ''} onChange={(e) => updateIntegration('zeptomail', 'from_email', e.target.value)} placeholder="noreply@edassist.co.in" className="mt-1" /></div>
                <div><Label>From Name</Label><Input value={settings.integrations?.zeptomail?.from_name || ''} onChange={(e) => updateIntegration('zeptomail', 'from_name', e.target.value)} placeholder="EdAssist" className="mt-1" /></div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={settings.integrations?.zeptomail?.enabled || false} onChange={(e) => updateIntegration('zeptomail', 'enabled', e.target.checked)} className="rounded" id="zeptomail-enabled" />
                <Label htmlFor="zeptomail-enabled" className="text-sm cursor-pointer">Enable ZeptoMail</Label>
              </div>
              <p className="text-xs text-slate-400">Get your API key from <a href="https://zeptomail.zoho.com/" target="_blank" rel="noreferrer" className="underline text-indigo-500">ZeptoMail Console</a></p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-blue-600" />
                <div>
                  <CardTitle className="text-base">Razorpay (Payment Gateway)</CardTitle>
                  <p className="text-xs text-slate-500 mt-0.5">Accept subscription payments from institutions</p>
                </div>
                <Badge className={settings.integrations?.razorpay?.enabled ? 'bg-green-100 text-green-700 ml-auto' : 'bg-slate-100 text-slate-500 ml-auto'}>
                  {settings.integrations?.razorpay?.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Key ID</Label><Input value={settings.integrations?.razorpay?.key_id || ''} onChange={(e) => updateIntegration('razorpay', 'key_id', e.target.value)} placeholder="rzp_test_..." className="mt-1" data-testid="cms-razorpay-key" /></div>
                <div><Label>Key Secret</Label><Input type="password" value={settings.integrations?.razorpay?.key_secret || ''} onChange={(e) => updateIntegration('razorpay', 'key_secret', e.target.value)} placeholder="Enter secret" className="mt-1" /></div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={settings.integrations?.razorpay?.enabled || false} onChange={(e) => updateIntegration('razorpay', 'enabled', e.target.checked)} className="rounded" id="razorpay-enabled" />
                <Label htmlFor="razorpay-enabled" className="text-sm cursor-pointer">Enable Razorpay</Label>
              </div>
              <p className="text-xs text-slate-400">Get your keys from <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="underline text-indigo-500">Razorpay Dashboard</a></p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CMSTab;
